/**
 * A recorded room's Questions: who is watching now, what waits by the part of the talk it was asked at, what was
 * answered, and what people answered inside the recording. Every answer goes into the asker's own Direct thread.
 */
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import type { HostEveryoneResponse, TalkAskedMoment, TalkQuestion, TalkQuestionChapter, TalkQuestionGroup, TalkQuestionsResponse, TalkTypedAnswer } from "../../shared/talk";
import { Button } from "../components/ui/Button";
import { Face, FaceStack } from "../components/ui/Face";
import { Icon } from "../components/ui/Icon";
import { useToast } from "../components/ui/Toast";
import { api } from "../lib/api";
import { type AnswerSend, PrivateSendSheet, startMessage } from "../views/rooms/PrivateSendSheet";
import { RoomSide } from "./Everyone";
import { faceOf, Head, Poster } from "./parts";
import { Nav } from "./phone";
import { directPath, placesPath, roomHome } from "./places";
import { askedWhen, chapterRange, chapterWaiting, firstWord, idsOf, momentKind, namesOf, takeOut, talkClock } from "./questionsView";
import { TalkSheet } from "./TalkSheet";
import { useTalkContext } from "./TalkLayout";
import { useHost } from "./useHost";
import { useRemote } from "./useTalk";
import { whenWords } from "./rooms";

type Tab = "waiting" | "answered" | "theirs";

/** Singles shown in a chapter before "Show N more". */
const SHOWN = 5;

/** The second of the talk something happened at. */
function MomentChip({ sec }: { sec: number | null }) {
  if (sec === null) return null;
  return (
    <span className="mchip">
      <Icon name="playf" /> At {talkClock(sec)}
    </span>
  );
}

/** The room's people with a playing playhead, and where each one is. A face opens that person's Direct thread. */
function WatchStrip({ data }: { data: TalkQuestionsResponse }) {
  const navigate = useNavigate();
  if (!data.watchingNow.length) return null;
  return (
    <div className="q-watch">
      <p className="q-watch-h">
        <i className="grp-now" /> Watching now · {data.watchingNow.length}
      </p>
      <div className="q-watch-l">
        {data.watchingNow.map((w) => (
          <button key={w.registrantId} type="button" className="q-wf" aria-label={`Open ${w.name}'s chat`} onClick={() => navigate(directPath(w.contactId))}>
            <Face name={w.name} photo={w.photoUrl} size={30} />
            <span>
              <b>{firstWord(w.name)}</b>
              <small>{talkClock(w.atSec)}</small>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** A reply written right where the line is: the words quoted above the box, and a Send that goes into that person's own chat. */
function InlineReply({ quote, name, note, onSend, onCancel, draft }: { quote: ReactNode; name: string; note: string; onSend: (text: string) => Promise<void>; onCancel: () => void; draft?: () => Promise<string> }) {
  const toast = useToast();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const draftIt = () => {
    if (!draft || drafting) return;
    setDrafting(true);
    draft()
      .then((t) => t && setText(t))
      .catch(() => toast({ text: "We couldn't draft that. Write it yourself for now.", icon: "info" }))
      .finally(() => setDrafting(false));
  };
  const box = useRef<HTMLTextAreaElement>(null);
  useEffect(() => box.current?.focus({ preventScroll: true }), []);
  const send = async () => {
    if (busy || !text.trim()) return;
    setBusy(true);
    try {
      await onSend(text.trim());
    } catch {
      setBusy(false);
    }
  };
  return (
    <div className="q-rep">
      <p className="q-rep-q">
        <Icon name="chat" />
        <span>{quote}</span>
      </p>
      <textarea ref={box} aria-label={`Reply to ${name}`} placeholder={`Reply to ${name}`} value={text} onChange={(e) => setText(e.target.value)} />
      <div className="q-rep-bar">
        <small>{note}</small>
        <span className="q-rep-act">
          {draft ? (
            <Button size="s" icon="sparkle" disabled={drafting} onClick={draftIt}>
              {drafting ? "Drafting..." : "Draft with AI"}
            </Button>
          ) : null}
          <button type="button" className="q-rep-x" onClick={onCancel}>
            Not now
          </button>
          <Button variant="p" size="s" icon="send" disabled={busy || !text.trim()} onClick={() => void send()}>
            Send
          </Button>
        </span>
      </div>
    </div>
  );
}

/** One question on its own: who, where in the talk, the words, when, and the way to answer it right here. */
function AskRow({ q, taken, onAnswer }: { q: TalkQuestion; taken: boolean; onAnswer: (q: TalkQuestion, text: string) => Promise<void> }) {
  const { wid = "" } = useParams();
  const [open, setOpen] = useState(false);
  const first = firstWord(q.name);
  return (
    <div className="q-row">
      <Face name={q.name} photo={q.photoUrl} size={40} />
      <div className="q-b">
        <b>
          {q.name}
          <MomentChip sec={q.atSec} />
        </b>
        <p>{q.body}</p>
        {q.watchingNow ? (
          <small className="q-now">
            <i className="grp-now" /> Watching now
          </small>
        ) : (
          <small>{askedWhen(q)}</small>
        )}
        {taken ? (
          <small className="q-taken">
            <Icon name="undo" /> Taken out of the group
          </small>
        ) : null}
        {open ? (
          <InlineReply
            quote={`${first}${q.atSec === null ? "" : ` at ${talkClock(q.atSec)}`}: ${q.body}`}
            name={first}
            note={`Only ${first} sees it, in their own chat with you.`}
            onSend={(text) => onAnswer(q, text).then(() => setOpen(false))}
            draft={() => api.talk.draftQuestions(wid, { messageIds: [q.messageId] }).then((r) => r.text)}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </div>
      {open ? null : (
        <Button size="s" icon="chat" aria-label={`Answer ${q.name}`} onClick={() => setOpen(true)}>
          Answer
        </Button>
      )}
    </div>
  );
}

/** Several people who asked the same thing: one card, one answer for all. Each line can be taken out. */
function SameCard({ group, onAnswerAll, onOut }: { group: TalkQuestionGroup; onAnswerAll: () => void; onOut: (id: string) => void }) {
  const asks = group.questions;
  return (
    <div className="q-same">
      <div className="q-same-h">
        <FaceStack people={asks.map(faceOf)} size={30} />
        <span className="q-same-t">
          <small>Asked the same</small>
          <b>
            {asks.length} asked about {group.label}
          </b>
        </span>
        <Button variant="p" size="s" icon="chat" onClick={onAnswerAll}>
          Answer all {asks.length}
        </Button>
      </div>
      <ul className="q-same-l">
        {asks.map((a) => (
          <li key={a.messageId}>
            <Face name={a.name} photo={a.photoUrl} size={22} />
            <b>{firstWord(a.name)}</b>
            <span className="q-at">{a.atSec === null ? "" : `At ${talkClock(a.atSec)}`}</span>
            <span className="q-q">{a.body}</span>
            <button type="button" className="q-ns" aria-label={`Not the same, ${a.name}`} onClick={() => onOut(a.messageId)}>
              Not the same
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A chapter of the talk and what waits in it: the cards of people who asked the same first, then the single questions. */
function Chapter({ c, onAnswer, onAnswerAll, onOut }: { c: TalkQuestionChapter; onAnswer: (q: TalkQuestion, text: string) => Promise<void>; onAnswerAll: (g: TalkQuestionGroup) => void; onOut: (id: string) => void }) {
  const [more, setMore] = useState(false);
  const shown = more ? c.single : c.single.slice(0, SHOWN);
  return (
    <section className="q-chap">
      <p className="q-ch">
        <span>{chapterRange(c)}</span> · <b>{c.title}</b> · <em>{chapterWaiting(c)}</em>
      </p>
      {c.groups.map((g) => (
        <SameCard key={g.id} group={g} onAnswerAll={() => onAnswerAll(g)} onOut={onOut} />
      ))}
      {shown.map((q) => (
        <AskRow key={q.messageId} q={q} taken={c.takenOut.includes(q.messageId)} onAnswer={onAnswer} />
      ))}
      {c.single.length > shown.length ? (
        <button type="button" className="q-more" onClick={() => setMore(true)}>
          Show {c.single.length - shown.length} more
        </button>
      ) : null}
    </section>
  );
}

function Answered({ list }: { list: TalkQuestion[] }) {
  const me = useHost();
  const [shown, setShown] = useState(20);
  if (!list.length) return <p className="empty-note">No question has an answer yet.</p>;
  return (
    <>
      {list.slice(0, shown).map((q) => (
        <div key={q.messageId} className="q-row done">
          <Face name={q.name} photo={q.photoUrl} size={40} />
          <div className="q-b">
            <b>
              {q.name}
              <MomentChip sec={q.atSec} />
            </b>
            <p>{q.body}</p>
            {q.answer ? (
              <>
                <p className="q-mine">
                  <Face name={me.name} photo={me.photo} size={20} />
                  {q.answer.body}
                </p>
                <small>Answered {whenWords(q.answer.at)}</small>
              </>
            ) : null}
          </div>
        </div>
      ))}
      {list.length > shown ? (
        <button type="button" className="q-more" onClick={() => setShown(shown + 20)}>
          Show {Math.min(20, list.length - shown)} more
        </button>
      ) : null}
    </>
  );
}

// --- Their answers: what the host asked inside the recording ---

/** A choice's "..." menu: message each privately, or start a circle with them. */
function ChoiceMenu({ label, open, onToggle, onMessage, onCircle }: { label: string; open: boolean; onToggle: () => void; onMessage: () => void; onCircle: () => void }) {
  const box = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => {
      const outside = e instanceof KeyboardEvent ? e.key === "Escape" : !box.current?.contains(e.target as Node);
      if (outside) onToggle();
    };
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", away);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", away);
    };
  }, [open, onToggle]);
  return (
    <span className="sp-more" ref={box}>
      <button type="button" className={open ? "sp-go on" : "sp-go"} aria-label={`${label}, menu`} aria-expanded={open} onClick={onToggle}>
        <Icon name="more" />
      </button>
      {open ? (
        <span className="sp-menu" role="menu">
          <button type="button" role="menuitem" onClick={() => (onToggle(), onMessage())}>
            <Icon name="chat" /> Message each privately
          </button>
          <button type="button" role="menuitem" onClick={() => (onToggle(), onCircle())}>
            <Icon name="target" /> Start a circle with them
          </button>
        </span>
      ) : null}
    </span>
  );
}

function TypedRow({ a, atSec, onReply }: { a: TalkTypedAnswer; atSec: number; onReply: (a: TalkTypedAnswer, text: string) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const first = firstWord(a.name);
  const done = sent || a.repliedAt !== null;
  return (
    <li className="q-ty">
      <Face name={a.name} photo={a.photoUrl} size={36} />
      <div className="q-ty-b">
        <b>{a.name}</b>
        <p>{a.text}</p>
        {done ? (
          <small className="q-ty-sent">
            <Icon name="check" /> Sent to {first}'s own chat
          </small>
        ) : null}
        {open && !done ? (
          <InlineReply
            quote={`${first} at ${talkClock(atSec)}: ${a.text}`}
            name={first}
            note={`Only ${first} sees it, in their own chat with you.`}
            onSend={(text) => onReply(a, text).then(() => (setSent(true), setOpen(false)))}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </div>
      {open || done ? null : (
        <Button size="s" icon="chat" aria-label={`Reply privately to ${a.name}`} onClick={() => setOpen(true)}>
          Reply privately
        </Button>
      )}
    </li>
  );
}

function Moment({ m, wid, onMessage, onCircle, onReply }: { m: TalkAskedMoment; wid: string; onMessage: (send: AnswerSend) => void; onCircle: (opt: { interactionId: string; label: string; count: number; at: number }) => void; onReply: (a: TalkTypedAnswer, text: string) => Promise<void> }) {
  const [menu, setMenu] = useState<string | null>(null);
  const [shown, setShown] = useState(5);
  const me = useHost();
  const head = (
    <header>
      <span className="q-th-k">{momentKind(m)}</span>
      <MomentChip sec={m.atSec} />
    </header>
  );
  if (m.kind === "poll") {
    const top = Math.max(1, ...m.options.map((o) => o.count));
    return (
      <article className="q-th">
        {head}
        <h3>{m.question}</h3>
        <small className="q-th-n">{m.answered} answered</small>
        <div className="q-th-bars">
          {m.options.map((o) => {
            const id = `${m.id}:${o.label}`;
            return (
              <div key={id} className={menu === id ? "q-th-o menu" : "q-th-o"}>
                <span className="poll-o">
                  <i style={{ width: `${(o.count / top) * 100}%` }} />
                  <b>{o.label}</b>
                  <em>{o.count}</em>
                </span>
                {o.count > 0 ? (
                  <ChoiceMenu
                    label={o.label}
                    open={menu === id}
                    onToggle={() => setMenu((now) => (now === id ? null : id))}
                    onMessage={() =>
                      onMessage({
                        answer: { webinarId: wid, interactionId: m.id, option: o.label },
                        title: `Message each of the ${o.count} privately`,
                        reach: o.count,
                        faces: o.faces.map((f) => ({ name: f.name, avatar_url: f.photoUrl })),
                        label: `Answered "${o.label}" at ${talkClock(m.atSec)}`,
                        subject: `About your answer: ${m.question}`,
                        message: startMessage(`You picked "${o.label}" when I asked: ${m.question}${/[?.!]$/.test(m.question) ? "" : "."}`, firstWord(me.name)),
                      })
                    }
                    onCircle={() => onCircle({ interactionId: m.id, label: o.label, count: o.count, at: m.atSec })}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      </article>
    );
  }
  if (m.kind === "question") {
    return (
      <article className="q-th">
        {head}
        <h3>{m.question}</h3>
        <small className="q-th-n">{m.answered} answered</small>
        <ul className="q-ty-l">
          {m.answers.slice(0, shown).map((a) => (
            <TypedRow key={a.responseId} a={a} atSec={m.atSec} onReply={onReply} />
          ))}
        </ul>
        {m.answers.length > shown ? (
          <button type="button" className="q-more" onClick={() => setShown(shown + 5)}>
            Show {Math.min(5, m.answers.length - shown)} more
          </button>
        ) : null}
      </article>
    );
  }
  return (
    <article className="q-th">
      {head}
      <h3>{m.prompt}</h3>
      <div className="q-re">
        {m.reactions.map((r) => (
          <span key={r.emoji} className="q-re-c">
            <b>{r.count}</b>
            <small>{r.emoji}</small>
          </span>
        ))}
      </div>
    </article>
  );
}

// --- the right panel: where they ask, this room, the groups ---

/** The talk as a strip: a dot for each waiting question where it was asked, and a tick where each chapter starts. */
function WhereMap({ data }: { data: TalkQuestionsResponse }) {
  const asks = data.chapters.flatMap((c) => [...c.groups.flatMap((g) => g.questions), ...c.single]).filter((q) => q.atSec !== null);
  const len = Math.max(1, data.lengthSec);
  const ticks = data.chapters.map((c) => c.startSec).filter((t) => t > 0);
  return (
    <div className="q-map">
      <div className="q-map-bar" role="img" aria-label={`${asks.length} waiting ${asks.length === 1 ? "question" : "questions"} across the ${Math.round(len / 60)} minutes`}>
        {ticks.map((t) => (
          <i key={t} className="q-tick" style={{ left: `${(t / len) * 100}%` }} />
        ))}
        {asks.map((q, i) => (
          <i key={q.messageId} className="q-dot" style={{ left: `${((q.atSec ?? 0) / len) * 100}%`, bottom: 4 + ((i * 7) % 24) }} />
        ))}
      </div>
      <div className="q-map-ax">
        {ticks.map((t) => (
          <span key={t} style={{ left: `${(t / len) * 100}%` }}>
            {talkClock(t)}
          </span>
        ))}
        <span className="r">{talkClock(len)}</span>
      </div>
    </div>
  );
}

function QuestionsSide({ wid, data, room, reloadRoom }: { wid: string; data: TalkQuestionsResponse; room: HostEveryoneResponse | null; reloadRoom: () => void }) {
  return (
    <>
      <h3>Where they ask</h3>
      <WhereMap data={data} />
      {room ? <RoomSide wid={wid} data={room} onChange={reloadRoom} /> : null}
    </>
  );
}

// --- Answer all who asked the same ---

/** The one answer that goes to each person privately: the host reads it and presses Send herself. */
function AnswerAllSheet({ wid, group, phone, onClose, onSent }: { wid: string; group: TalkQuestionGroup; phone: boolean; onClose: () => void; onSent: () => void }) {
  const toast = useToast();
  // Why kept at open: a question grouped after the sheet opened is not in this answer.
  const [asks] = useState(group.questions);
  const [ids] = useState(() => idsOf(asks));
  const [requestId] = useState(() => crypto.randomUUID());
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const at = asks.map((a) => a.atSec).filter((s): s is number => s !== null).toSorted((a, b) => a - b)[0];

  const draft = () => {
    if (drafting) return;
    setDrafting(true);
    api.talk
      .draftQuestions(wid, { messageIds: ids })
      .then((r) => r.text && setText(r.text))
      .catch(() => toast({ text: "We couldn't draft that. Write it yourself for now.", icon: "info" }))
      .finally(() => setDrafting(false));
  };
  const send = () => {
    if (busy || !text.trim()) return;
    setBusy(true);
    api.talk
      .answerQuestions(wid, { messageIds: ids, body: text.trim(), requestId })
      .then((res) => {
        toast({ text: `Sent to ${res.sent.toLocaleString("en-US")} ${res.sent === 1 ? "person" : "people"}`, icon: "send" });
        onSent();
        onClose();
      })
      .catch(() => {
        toast({ text: "We couldn't send that. Try again.", icon: "info" });
        setBusy(false);
      });
  };

  return (
    <TalkSheet
      phone={phone}
      title={`Answer all ${asks.length}, each privately`}
      lead={`They asked about ${group.label}${at === undefined ? "." : `, at ${talkClock(at)} or near it.`}`}
      onClose={onClose}
      primary={{ label: `Send to all ${asks.length}`, icon: "send", disabled: busy || !text.trim(), onClick: send }}
    >
      <ul className="q-aa-l">
        {asks.map((a) => (
          <li key={a.messageId}>
            <Face name={a.name} photo={a.photoUrl} size={28} />
            <span className="q-aa-t">
              <b>
                {a.name}
                <MomentChip sec={a.atSec} />
              </b>
              <span>{a.body}</span>
            </span>
          </li>
        ))}
      </ul>
      <div className="q-aa-box">
        <textarea className="q-aa-txt" aria-label="Your answer" rows={4} placeholder="Write one answer for all of them" value={text} onChange={(e) => setText(e.target.value)} />
        <div className="q-aa-bar">
          <Button size="s" icon="sparkle" disabled={drafting} onClick={draft}>
            {drafting ? "Drafting..." : "Draft with AI"}
          </Button>
        </div>
      </div>
      <p className="q-aa-to">
        <Icon name="mail" />
        <span>{namesOf(asks.map((a) => a.name))} each get it in their own chat, and by email if they have left.</span>
      </p>
    </TalkSheet>
  );
}

// --- the screen ---

export function QuestionsScreen() {
  const { wid = "" } = useParams();
  const ctx = useTalkContext();
  const phone = ctx.lay === "phone";
  const upright = ctx.lay === "upright";
  const toast = useToast();
  const q = useRemote<TalkQuestionsResponse>(`talk:questions:${wid}`, () => api.talk.questions(wid), `/talk/rooms/${encodeURIComponent(wid)}/ws`);
  const answers = useRemote(`talk:answers:${wid}`, () => api.talk.answers(wid));
  const room = useRemote(`talk:everyone:${wid}`, () => api.talk.everyone(wid));
  const [tab, setTab] = useState<Tab>("waiting");
  const [answering, setAnswering] = useState<TalkQuestionGroup | null>(null);
  const [sending, setSending] = useState<AnswerSend | null>(null);
  const reloadAll = () => {
    void q.reload();
    ctx.reloadRail();
  };

  if (!q.data) return q.phase === "error" ? <main className="main"><p className="empty-note">We couldn't open the questions. Pick them again.</p></main> : <main className="main" />;
  const data = q.data;
  if (data.room.questionsWaiting === null) return <Navigate to={roomHome(data.room)} replace />;

  const answerOne = async (ask: TalkQuestion, body: string) => {
    try {
      await api.talk.answerQuestions(wid, { messageIds: [ask.messageId], body, requestId: crypto.randomUUID() });
      toast({ text: `Sent to ${firstWord(ask.name)}'s own chat`, icon: "send" });
      reloadAll();
    } catch (err) {
      toast({ text: err instanceof Error && err.message ? err.message : "We couldn't send that. Try again.", icon: "info" });
      throw err;
    }
  };
  const replyPrivately = async (a: TalkTypedAnswer, body: string) => {
    try {
      await api.talk.sendDirect(a.contactId, { channel: "chat", body, replyAnswer: a.responseId, clientId: crypto.randomUUID() });
      toast({ text: `Sent to ${firstWord(a.name)}'s own chat`, icon: "send" });
      void answers.reload();
    } catch {
      toast({ text: "We couldn't send that. Try again.", icon: "info" });
      throw new Error("not sent");
    }
  };
  const notSame = (id: string) => {
    q.setData((d) => (d ? takeOut(d, id) : d));
    api.talk.notSame(wid, id).then(
      () => void q.reload(),
      () => {
        toast({ text: "We couldn't take that out. Try again.", icon: "info" });
        void q.reload();
      },
    );
  };

  const moments = answers.data?.moments ?? [];
  const tabs: [Tab, string, number | null][] = [
    ["waiting", "Waiting", data.waiting],
    ["answered", "Answered", data.answered],
    ["theirs", "Their answers", answers.data ? moments.length : null],
  ];
  const sub = `${data.room.title} · ${data.waiting.toLocaleString("en-US")} waiting · ${data.answered.toLocaleString("en-US")} answered`;
  const chapters = data.chapters.filter((c) => chapterWaiting(c) > 0);
  const side = <QuestionsSide wid={wid} data={data} room={room.data} reloadRoom={() => void room.reload()} />;

  const body = (
    <>
      <div className="gseg q-tabs" role="tablist" aria-label="Questions">
        {tabs.map(([k, label, n]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? "on" : undefined} onClick={() => setTab(k)}>
            {label}
            {n === null ? null : <b>{n.toLocaleString("en-US")}</b>}
          </button>
        ))}
      </div>
      {tab === "theirs" ? null : <WatchStrip data={data} />}
      <div className="q-list">
        {tab === "waiting" ? (
          chapters.length ? (
            chapters.map((c) => <Chapter key={c.startSec} c={c} onAnswer={answerOne} onAnswerAll={setAnswering} onOut={notSame} />)
          ) : (
            <p className="empty-note">Nothing is waiting. Every question has an answer.</p>
          )
        ) : tab === "answered" ? (
          <Answered list={data.answeredList} />
        ) : moments.length ? (
          moments.map((m) => (
            <Moment
              key={m.id}
              m={m}
              wid={wid}
              onMessage={setSending}
              onCircle={(o) =>
                ctx.startCircle(wid, undefined, undefined, {
                  answer: { webinarId: wid, interactionId: o.interactionId, option: o.label },
                  count: o.count,
                  faces: [],
                  words: `Answered "${o.label}" at ${talkClock(o.at)}`,
                })
              }
              onReply={replyPrivately}
            />
          ))
        ) : (
          <p className="empty-note">{answers.data ? "You have not asked anything inside this talk yet." : ""}</p>
        )}
        {phone || upright ? <div className="q-fold side">{side}</div> : null}
      </div>
    </>
  );
  const sheets = (
    <>
      {answering ? <AnswerAllSheet wid={wid} group={answering} phone={phone} onClose={() => setAnswering(null)} onSent={reloadAll} /> : null}
      {sending ? <PrivateSendSheet key={sending.answer.interactionId + sending.answer.option} send={sending} onClose={() => setSending(null)} onAnswerSent={() => void answers.reload()} /> : null}
    </>
  );

  if (phone) {
    return (
      <div className="hp">
        <Nav back="Room" to={placesPath(wid)} title="Questions" sub={`${data.waiting.toLocaleString("en-US")} waiting · ${data.answered.toLocaleString("en-US")} answered`} />
        <div className="hp-scroll">
          <div className="hp-page q-page">{body}</div>
        </div>
        {sheets}
      </div>
    );
  }
  return (
    <>
      <main className="main">
        <Head pic={<Poster url={data.room.posterUrl} className="mh-pic" />} title="Questions" sub={sub} />
        <div className="qmain">{body}</div>
      </main>
      {upright ? null : <aside className="side">{side}</aside>}
      {sheets}
    </>
  );
}
